'use client';

import { useState, useEffect, useRef, RefObject } from 'react';

/**
 * React hook that returns whether the given element or any of its descendants have keyboard-visible focus.
 */
export const useFocusVisibleWithin = (ref: RefObject<HTMLElement | null>) => {
  const [isFocusVisibleWithin, setIsFocusVisibleWithin] = useState(false);
  const hadKeyboard = useRef(false);

  useEffect(() => {
    const handleKeyDown = () => {
      hadKeyboard.current = true;
    };
    const handleMouseDown = () => {
      hadKeyboard.current = false;
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('mousedown', handleMouseDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('mousedown', handleMouseDown);
    };
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const handleFocusIn = () => {
      if (hadKeyboard.current) {
        setIsFocusVisibleWithin(true);
      }
    };
    const handleFocusOut = (event: FocusEvent) => {
      if (!el.contains(event.relatedTarget as Node)) {
        setIsFocusVisibleWithin(false);
      }
    };

    el.addEventListener('focusin', handleFocusIn);
    el.addEventListener('focusout', handleFocusOut);

    return () => {
      el.removeEventListener('focusin', handleFocusIn);
      el.removeEventListener('focusout', handleFocusOut);
    };
  }, [ref]);

  return isFocusVisibleWithin;
};
